/**
 * Main App component for Schiffer Digital
 */

import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { authAPI, isAuthenticated } from './services/api';

// Components
import Login from './components/Login/Login';
import Layout from './components/Layout/Layout';
import Dashboard from './components/Dashboard/Dashboard';
import Customers from './components/Customers/Customers';
import CustomerDetail from './components/Customers/CustomerDetail';
import CustomerForm from './components/Customers/CustomerForm';
import Invoices from './components/Invoices/Invoices';
import InvoiceDetail from './components/Invoices/InvoiceDetail';
import InvoiceForm from './components/Invoices/InvoiceForm';
import Offers from './components/Offers/Offers';
import OfferForm from './components/Offers/OfferForm';
import DatevExport from './components/Datev/DatevExport';
import Expenses from './components/Expenses/Expenses';
import IncomingInvoices from './components/Invoices/IncomingInvoices';
import CompanySettings from './components/Settings/CompanySettings';
import EmailSettings from './components/Settings/EmailSettings';
import LoadingSpinner from './components/Common/LoadingSpinner';

// Styles
import './App.css';

// Protected Route component
const ProtectedRoute = ({ children }) => {
  return isAuthenticated() ? children : <Navigate to="/login" replace />;
};

// Public Route component (redirect to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  return !isAuthenticated() ? children : <Navigate to="/dashboard" replace />;
};

function App() {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Check authentication status on app load
    const checkAuth = async () => {
      if (isAuthenticated()) {
        try {
          const response = await authAPI.getCurrentUser();
          setUser(response.data);
        } catch (error) {
          console.error('Auth check failed:', error);
          // Token might be invalid, let the interceptor handle it
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  if (loading) {
    return (
      <div className="app-loading">
        <LoadingSpinner size="large" />
        <p>Lade Schiffer Digital...</p>
      </div>
    );
  }

  return (
    <div className="App">
      <Router>
        <Routes>
          {/* Public routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login onLogin={setUser} />
              </PublicRoute>
            }
          />

          {/* Protected routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout user={user} onLogout={() => setUser(null)} />
              </ProtectedRoute>
            }
          >
            {/* Dashboard */}
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />

            {/* Customers */}
            <Route path="customers" element={<Customers />} />
            <Route path="customers/new" element={<CustomerForm />} />
            <Route path="customers/:id" element={<CustomerDetail />} />
            <Route path="customers/:id/edit" element={<CustomerForm />} />

            {/* Invoices */}
            <Route path="invoices" element={<Invoices />} />
            <Route path="invoices/new" element={<InvoiceForm />} />
            <Route path="invoices/:id" element={<InvoiceDetail />} />
            <Route path="invoices/:id/edit" element={<InvoiceForm />} />

            {/* Offers (Angebote) */}
            <Route path="offers" element={<Offers />} />
            <Route path="offers/new" element={<OfferForm />} />
            <Route path="offers/:id/edit" element={<OfferForm />} />

            {/* Incoming Invoices (Eingangsrechnungen) */}
            <Route path="incoming" element={<IncomingInvoices />} />

            {/* Expenses (Ausgaben) */}
            <Route path="expenses" element={<Expenses />} />

            {/* DATEV Export */}
            <Route path="datev" element={<DatevExport />} />

            {/* Company settings */}
            <Route path="settings" element={<CompanySettings />} />
            <Route path="email" element={<EmailSettings />} />
          </Route>

          {/* Fallback route */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Router>

      {/* Global toast notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            style: {
              background: '#10b981',
            },
          },
          error: {
            style: {
              background: '#ef4444',
            },
          },
        }}
      />
    </div>
  );
}

export default App;